// API client for admin.onlytrade.ai
// Hits /api/* which nginx proxies to the admin backend (shared with admin.kommercio.io).
// The admin backend already owns products / tiers / features / entitlements / subscriptions,
// so OnlyTrade admin changes are canonical — the same DB the Kommercio admin edits.

const TOKEN_KEY = 'onlytrade_admin_token';
const REFRESH_KEY = 'onlytrade_admin_refresh';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}
export function getRefresh(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}
export function setRefresh(token: string | null) {
  if (token) localStorage.setItem(REFRESH_KEY, token);
  else localStorage.removeItem(REFRESH_KEY);
}

// Ask the auth-service to rotate and issue a fresh access token (it reads the
// refresh cookie). If that fails, return null — the caller bounces to /login.
async function refreshAccessToken(): Promise<string | null> {
  try {
    const resp = await fetch('/auth/sessions/refresh', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!resp.ok) return null;
    const data = await resp.json();
    if (data.access) { setToken(data.access); return data.access; }
  } catch {}
  return null;
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
  _retry = false
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const resp = await fetch(`/api${path}`, { ...init, headers });

  if (resp.status === 401 && !_retry) {
    const fresh = await refreshAccessToken();
    if (fresh) return api<T>(path, init, true);
    setToken(null); setRefresh(null);
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  if (!resp.ok) {
    const body = await resp.text().catch(() => '');
    throw new Error(body || `HTTP ${resp.status} ${resp.statusText}`);
  }
  if (resp.status === 204) return undefined as T;
  const ct = resp.headers.get('content-type') || '';
  if (!ct.includes('application/json')) return undefined as T;
  return resp.json() as Promise<T>;
}

// Legacy password login has been replaced by passkey auth.
// Auth flows live in src/auth.ts; this module just provides API calls.

// ─── Types ──────────────────────────────────────────────────────────────────
export type Product = {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  domain: string | null;
  status: 'active' | 'inactive' | string;
  active_subscribers: number;
  tiers: Tier[];
};

export type Tier = {
  id: number;
  product_id: number;
  name: string;
  slug: string;
  price_monthly: number;          // stored in pence
  price_yearly?: number | null;
  features_json?: string | string[] | null;
  display_order?: number;
  is_active?: boolean;
};

export type Feature = {
  id: number;
  product_id: number;
  feature_key: string;
  feature_name: string;
  feature_description: string | null;
  feature_type: 'boolean' | 'limit' | string;
  display_order: number;
};

export type EntitlementCell = { enabled: boolean; limit_value: number | null };
export type EntitlementsMatrix = {
  features: Feature[];
  tiers: Tier[];
  entitlements: Record<number, Record<number, EntitlementCell>>; // tier_id -> feature_id -> cell
};

// ─── Product CRUD ───────────────────────────────────────────────────────────
export const products = {
  list: () => api<Product[]>('/products'),
  get: (id: number) => api<Product>(`/products/${id}`),
  create: (body: Partial<Product>) =>
    api<Product>('/products', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<Product>) =>
    api<Product>(`/products/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
};

// ─── Tier CRUD ──────────────────────────────────────────────────────────────
export const tiers = {
  create: (body: Partial<Tier> & { product_id: number; name: string; price_monthly: number }) =>
    api<Tier>('/tiers', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<Tier>) =>
    api<Tier>(`/tiers/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  archive: (id: number) =>
    api<{ message: string }>(`/tiers/${id}`, { method: 'DELETE' }),
};

// ─── Feature CRUD ───────────────────────────────────────────────────────────
export const features = {
  listForProduct: (productId: number) =>
    api<Feature[]>(`/products/${productId}/features`),
  create: (productId: number, body: Partial<Feature>) =>
    api<Feature>(`/products/${productId}/features`, { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<Feature>) =>
    api<Feature>(`/features/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id: number) =>
    api<{ message: string }>(`/features/${id}`, { method: 'DELETE' }),
};

// ─── Entitlements matrix ────────────────────────────────────────────────────
export const entitlements = {
  matrix: (productId: number) =>
    api<EntitlementsMatrix>(`/products/${productId}/entitlements-matrix`),
  bulkSetTier: (updates: Array<{ tier_id: number; feature_id: number; enabled: boolean; limit_value: number | null }>) =>
    api<unknown>('/tier-entitlements', { method: 'PUT', body: JSON.stringify(updates) }),
};

// ─── Subscriptions ──────────────────────────────────────────────────────────
export type Subscription = {
  id: number | string;
  tenant_id: string;
  tenant_name: string;
  product_id: number;
  product_name: string;
  tier_id: number;
  tier_name: string;
  status: string;
  price_monthly: number;
  current_period_end: string | null;
  trial_ends_at: string | null;
};

export const subscriptions = {
  list: (params: { page?: number; limit?: number; productId?: number; status?: string; tierId?: number; search?: string } = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') qs.set(k, String(v)); });
    return api<{ data: Subscription[]; total: number; totalPages: number; page: number }>(`/subscriptions?${qs}`);
  },
  update: (id: string | number, body: Partial<Subscription>) =>
    api<Subscription>(`/subscriptions/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
};

// ─── Tenants (SaaS customers) ───────────────────────────────────────────────
export type Tenant = {
  id: number;
  business_id?: string;
  business_name: string;
  email: string;
  status: string;
  created_at: string;
  product_name?: string;
  tier_name?: string;
};

export const tenants = {
  list: (params: { page?: number; limit?: number; search?: string; status?: string; productId?: number } = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== '') qs.set(k, String(v)); });
    return api<{ data: Tenant[]; total: number; totalPages: number; page: number }>(`/tenants?${qs}`);
  },
  get: (id: number) => api<Tenant & { subscriptions?: Subscription[] }>(`/tenants/${id}`),
  entitlements: (id: number) => api<unknown>(`/tenants/${id}/entitlements`),
  addOverride: (id: number, body: { feature_key: string; enabled: boolean; limit_value?: number | null; reason?: string; expires_at?: string | null }) =>
    api<unknown>(`/tenants/${id}/entitlement-overrides`, { method: 'POST', body: JSON.stringify(body) }),
  removeOverride: (overrideId: number) =>
    api<{ message: string }>(`/entitlement-overrides/${overrideId}`, { method: 'DELETE' }),
};

// ─── OnlyTrade product-side data (Jobs / Customers / Quotes) ───────────────
// These hit the existing OnlyTrade Express app via nginx /onlytrade-api/* proxy.
export async function otApi<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  const resp = await fetch(`/onlytrade-api${path}`, { ...init, headers });
  if (!resp.ok) {
    const body = await resp.text().catch(() => '');
    throw new Error(body || `HTTP ${resp.status}`);
  }
  if (resp.status === 204) return undefined as T;
  return resp.json() as Promise<T>;
}

// ─── Calculators (admin-controlled registry — backed by OnlyTrade :3004) ───
// Phase 1a: toggle on/off + edit display name/description + admin note.
// Phase 2 will add formula/field editing per migrated trade (plumber, heating).
export type AdminCalculator = {
  calc_id: string;
  trade_key: string;                 // primary trade — drives the summary chips
  secondary_trades: string[];        // additional trades the calc also belongs to
  is_active: boolean;
  is_curated: boolean;
  display_name: string | null;     // null = use code default
  display_desc: string | null;     // null = use code default
  help_text: string | null;        // null = no help panel rendered
  admin_note: string | null;
  modified_by: string | null;
  modified_at: string;
  created_at: string;
};
export type CalcTradeSummary = {
  trade_key: string;
  total: number;
  active: number;
  curated: number;
};

export const calculators = {
  list: () => otApi<{ calculators: AdminCalculator[]; summary: CalcTradeSummary[] }>('/admin/calculators'),
  update: (calcId: string, body: Partial<Pick<AdminCalculator, 'is_active' | 'is_curated' | 'trade_key' | 'secondary_trades' | 'display_name' | 'display_desc' | 'help_text' | 'admin_note'>>) =>
    otApi<AdminCalculator>(`/admin/calculators/${calcId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  bulk: (trade_key: string, is_active: boolean) =>
    otApi<{ updated: number; calc_ids: string[] }>('/admin/calculators/bulk', { method: 'POST', body: JSON.stringify({ trade_key, is_active }) }),
};

// ─── Certificate types (admin-controlled registry — backed by OnlyTrade :3004) ───
// Layer 2 of the cert refactor: rename certs, retag trade, gate by tier, leave
// admin notes. The catalogue itself still lives in cert-configs.js (CERT_META);
// admins layer overrides on top via cert_type_overrides.
export type AdminCertType = {
  cert_type: string;
  code_label: string;
  display_name: string | null;
  icon: string | null;
  category: string | null;
  url: string | null;
  is_template: boolean;
  has_custom_page: boolean;
  trade_key: string;
  secondary_trades: string[];
  is_enabled: boolean;
  min_tier_id: number | null;
  admin_note: string | null;
  has_override: boolean;
  modified_by: string | null;
  modified_at: string | null;
};
export type CertTradeSummary = {
  trade_key: string;
  total: number;
  enabled: number;
  gated: number;
};
export type CertTier = {
  id: number;
  name: string;
  price_monthly: number | string;
};

export const certTypes = {
  list: () => otApi<{ cert_types: AdminCertType[]; summary: CertTradeSummary[]; tiers: CertTier[] }>('/admin/cert-types'),
  update: (certType: string, body: Partial<Pick<AdminCertType, 'is_enabled' | 'display_name' | 'trade_key' | 'secondary_trades' | 'min_tier_id' | 'admin_note'>>) =>
    otApi<AdminCertType>(`/admin/cert-types/${certType}`, { method: 'PATCH', body: JSON.stringify(body) }),
};

// ─── Cert type groups (admin-curated picker placards) ──────────────────────
export type CertGroup = {
  id: number;
  slug: string;
  label: string;
  icon: string | null;
  display_order: number;
  is_enabled: boolean;
  created_at: string;
  updated_at: string;
  members: string[];   // cert_type keys
};
export type CertCatalogueItem = {
  cert_type: string;
  label: string;
  icon: string | null;
  category: string;
};

export const certGroups = {
  list: () => otApi<{ groups: CertGroup[]; catalogue: CertCatalogueItem[] }>('/admin/cert-type-groups'),
  create: (body: { slug: string; label: string; icon?: string | null; display_order?: number; is_enabled?: boolean }) =>
    otApi<{ group: CertGroup }>('/admin/cert-type-groups', { method: 'POST', body: JSON.stringify(body) }),
  update: (id: number, body: Partial<Pick<CertGroup, 'label' | 'icon' | 'display_order' | 'is_enabled'>>) =>
    otApi<{ group: CertGroup }>(`/admin/cert-type-groups/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  remove: (id: number) =>
    otApi<{ ok: true }>(`/admin/cert-type-groups/${id}`, { method: 'DELETE' }),
  setMembers: (id: number, cert_types: string[]) =>
    otApi<{ ok: true; members: string[] }>(`/admin/cert-type-groups/${id}/members`, { method: 'PUT', body: JSON.stringify({ cert_types }) }),
};

// ─── Feature flags (platform-wide on/off + admin editor link) ───────────────
export type FeatureFlag = {
  feature_key: string;
  label: string;
  description: string | null;
  category: string;
  admin_url: string | null;
  is_enabled: boolean;
  display_order: number;
  updated_at: string;
  updated_by: string | null;
};

export const featureFlags = {
  list: () => otApi<{ flags: FeatureFlag[] }>('/admin/feature-flags'),
  update: (key: string, body: Partial<Pick<FeatureFlag, 'label' | 'description' | 'category' | 'admin_url' | 'is_enabled' | 'display_order'>>) =>
    otApi<{ flag: FeatureFlag }>(`/admin/feature-flags/${encodeURIComponent(key)}`, { method: 'PATCH', body: JSON.stringify(body) }),
};

// ─── Document preview (render any cert/doc PDF from sample data) ────────────
// Posts sample data to the doc-preview endpoint (backed by lib/doc-renderer on
// OnlyTrade :3004) and opens the returned PDF in a new tab. Reusable by any
// admin surface that wants a "render real PDF" button — pass doc_type + data.
export async function previewDoc(payload: {
  doc_type?: string;
  cert_data?: Record<string, unknown>;
  appliances?: Array<Record<string, unknown>>;
  audience?: 'engineer' | 'customer';
  defect_index?: number;
}): Promise<void> {
  const token = getToken();
  const resp = await fetch('/api/admin/voice-dialog/preview-pdf', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });
  if (!resp.ok) {
    let msg = `HTTP ${resp.status}`;
    try { msg = (await resp.json()).error || msg; } catch { /* not json */ }
    throw new Error(msg);
  }
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

// List the cert types the preview endpoint can render.
export function previewDocTypes() {
  return api<{ doc_types: Array<{ doc_type: string; label: string; orientation: string }> }>(
    '/admin/voice-dialog/doc-types'
  );
}

// Render a doc PDF and hand back an object URL (blob:) instead of opening a
// tab — so a caller can embed it live in an <iframe>. Same endpoint and
// payload as previewDoc(). Caller is responsible for URL.revokeObjectURL().
export async function renderDocBlobUrl(payload: {
  doc_type?: string;
  cert_data?: Record<string, unknown>;
  appliances?: Array<Record<string, unknown>>;
  audience?: 'engineer' | 'customer';
  defect_index?: number;
}): Promise<string> {
  const token = getToken();
  const resp = await fetch('/api/admin/voice-dialog/preview-pdf', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
  });
  if (!resp.ok) {
    let msg = `HTTP ${resp.status}`;
    try { msg = (await resp.json()).error || msg; } catch { /* not json */ }
    throw new Error(msg);
  }
  const blob = await resp.blob();
  return URL.createObjectURL(blob);
}
